import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {ConfigService} from './config.service';
import {Store} from '@ngxs/store';
import {AuthState} from './shared/states/AuthState';
import {SortDirection} from '@angular/material/sort';
import {User, AmaiaResponse, FilteredResponse, ImgurResponse, Role, Permission, Post} from 'jugacu-lib';

@Injectable({
    providedIn: 'root'
})
export class WhistlerAPIService {

    constructor(
        private readonly httpClient: HttpClient,
        private readonly configService: ConfigService,
        private readonly store: Store,
    ) {
    }

    private get token(): string {
        return this.store.selectSnapshot(AuthState.token);
    }

    public getRequestedUser(name: string, token = this.token): Promise<AmaiaResponse<User>> {
        return this.httpClient
            .get<AmaiaResponse<User>>(
                `${this.configService.config.AMAIA_API_URL}/user/${name}`,
                {
                    headers: new HttpHeaders({
                        Authorization: `Bearer ${token}`,
                    })
                }
            )
            .toPromise();
    }

    public getUsers(
        offset = 0,
        sortDirection: SortDirection,
        filter?: string,
        token = this.token
    ): Promise<AmaiaResponse<FilteredResponse<User[]>>> {
        return this.httpClient
            .post<AmaiaResponse<FilteredResponse<User[]>>>
            (`${this.configService.config.AMAIA_API_URL}/users/get/${offset}/${sortDirection}`, {
                filter
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            }).toPromise();
    }

    public getAmaiaUserCount(token = this.token): Promise<AmaiaResponse<number>> {
        return this.httpClient
            .get<AmaiaResponse<number>>(`${this.configService.config.AMAIA_API_URL}/users/count`, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            }).toPromise();
    }

    public uploadToImgur(file: File): Promise<ImgurResponse> {
        const formData = new FormData();
        formData.append('image', file);

        return this.httpClient
            .post<ImgurResponse>(`https://api.imgur.com/3/image`, formData, {
                headers: new HttpHeaders({
                    Authorization: `Client-ID ${this.configService.config.IMGUR_AUTH}`,
                })
            }).toPromise();
    }

    public getRoleList(token = this.token): Promise<AmaiaResponse<Role[]>> {
        return this.httpClient
            .get<AmaiaResponse<Role[]>>(`${this.configService.config.AMAIA_API_URL}/roles/get`, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            })
            .toPromise();
    }

    public addRoleToUser(user: User, role: Role, token = this.token): Promise<AmaiaResponse<Role[]>> {
        return this.httpClient
            .put<AmaiaResponse<Role[]>>(`${this.configService.config.AMAIA_API_URL}/user/${user.name}/roles/add`, {
                role: role.name
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            })
            .toPromise();
    }

    public removeRoleFromUser(user: User, role: Role, token = this.token): Promise<AmaiaResponse<Role[]>> {
        return this.httpClient
            .post<AmaiaResponse<Role[]>>(`${this.configService.config.AMAIA_API_URL}/user/${user.name}/roles/delete`, {
                role: role.name
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            })
            .toPromise();
    }

    public getPermissionList(token = this.token): Promise<AmaiaResponse<Permission[]>> {
        return this.httpClient
            .get<AmaiaResponse<Permission[]>>(`${this.configService.config.AMAIA_API_URL}/permissions/get`, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            })
            .toPromise();
    }

    public addPermissionToUser(user: User, permission: Permission, token = this.token): Promise<AmaiaResponse<Permission[]>> {
        return this.httpClient
            .put<AmaiaResponse<Permission[]>>
            (`${this.configService.config.AMAIA_API_URL}/user/${user.name}/permissions/add`, {
                permission: permission.name
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            })
            .toPromise();
    }

    public removePermissionFromUser(user: User, permission: string, token = this.token): Promise<AmaiaResponse<Permission[]>> {
        return this.httpClient
            .post<AmaiaResponse<Permission[]>>
            (`${this.configService.config.AMAIA_API_URL}/user/${user.name}/permissions/delete`, {
                permission
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            })
            .toPromise();
    }

    public saveUser(user: User, token = this.token): Promise<AmaiaResponse<User>> {
        const {avatar, desc, password} = user;
        return this.httpClient
            .post<AmaiaResponse<User>>
            (`${this.configService.config.AMAIA_API_URL}/user/${user.name}/settings`, {
                avatar,
                desc,
                password,
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            })
            .toPromise();
    }

    public saveRole(role: Role, token = this.token): Promise<AmaiaResponse<Role>> {
        return this.httpClient
            .put<AmaiaResponse<Role>>(`${this.configService.config.AMAIA_API_URL}/roles/save`, role, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`
                })
            })
            .toPromise();
    }

    public createRole(role: Role, token = this.token): Promise<AmaiaResponse<Role>> {
        return this.httpClient
            .post<AmaiaResponse<Role>>(`${this.configService.config.AMAIA_API_URL}/roles/create`, role, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`
                })
            })
            .toPromise();
    }

    public deleteRole(role: Role, token = this.token): Promise<AmaiaResponse<Role>> {
        return this.httpClient
            .post<AmaiaResponse<Role>>(`${this.configService.config.AMAIA_API_URL}/roles/delete`, {
                name: role.name
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`
                })
            })
            .toPromise();
    }

    public getFilteredPosts(
        offset = 0,
        sortDirection: SortDirection,
        filter?: string,
        token = this.token
    ): Promise<AmaiaResponse<FilteredResponse<Post[]>>> {
        return this.httpClient.post<AmaiaResponse<FilteredResponse<Post[]>>>(
            `${this.configService.config.AMAIA_API_URL}/idc/posts/${offset}/${sortDirection}`, {
                filter
            }, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                })
            }).toPromise();
    }
}
